const AVIF_BRANDS = new Set(['avif'])
const SEQUENCE_BRAND = 'avis'
const MAX_FTYP_BOX_BYTES = 4 * 1024
const MAX_AVIF_FILE_BYTES = 50 * 1024 * 1024
const MAX_META_BOX_BYTES = 1024 * 1024
const MAX_TOP_LEVEL_BOXES = 10_000
const MAX_DECODE_ITEMS = 32
const MAX_ITEM_INFO_ENTRIES = 256
const MAX_EXTENTS_PER_ITEM = 16
const MAX_SEQUENCE_HEADER_SCAN_BYTES = 64 * 1024
const ALPHA_AUXILIARY_TYPE = 'urn:mpeg:mpegB:cicp:systems:auxiliary:alpha'

export function validateAvifDimensions(
  width: number,
  height: number,
  maxPixels: number,
): boolean {
  return width > 0 && height > 0 && width <= Math.floor(maxPixels / height)
}

function readFourCc(bytes: Uint8Array, offset: number): string {
  return String.fromCharCode(
    bytes[offset] ?? 0,
    bytes[offset + 1] ?? 0,
    bytes[offset + 2] ?? 0,
    bytes[offset + 3] ?? 0,
  )
}

export async function hasAvifFileType(file: File, signal?: AbortSignal): Promise<boolean> {
  if (file.size < 8) return false

  const initialHeader = await readFileSlice(file, 0, Math.min(file.size, 16), signal)
  const view = new DataView(initialHeader.buffer, initialHeader.byteOffset, initialHeader.byteLength)
  if (readFourCc(initialHeader, 4) !== 'ftyp') return false

  const shortSize = view.getUint32(0)
  const headerSize = shortSize === 1 ? 16 : 8
  if (initialHeader.length < headerSize) return false

  const boxSize = shortSize === 1
    ? Number(view.getBigUint64(8))
    : shortSize === 0
      ? file.size
      : shortSize
  if (
    !Number.isSafeInteger(boxSize) ||
    boxSize < headerSize + 8 ||
    boxSize > file.size ||
    boxSize > MAX_FTYP_BOX_BYTES
  ) return false
  // After the major brand and minor_version, compatible brands are complete
  // four-byte entries. Reject a truncated tail before native decode sees it.
  if ((boxSize - (headerSize + 8)) % 4 !== 0) return false

  const header = await readFileSlice(file, 0, boxSize, signal)
  if (header.length !== boxSize) return false

  const majorBrand = readFourCc(header, headerSize)
  if (majorBrand === SEQUENCE_BRAND) return false
  let hasAvifBrand = majorBrand === 'avif'
  // The four bytes after the major brand are minor_version, not a brand.
  // Only the following compatible-brand entries may affect the brand check.
  for (let offset = headerSize + 8; offset + 4 <= header.length; offset += 4) {
    const brand = readFourCc(header, offset)
    if (brand === SEQUENCE_BRAND) return false
    if (AVIF_BRANDS.has(brand)) hasAvifBrand = true
  }

  return hasAvifBrand
}

export async function readAvifDimensions(
  file: File,
  maxPixels = Number.MAX_SAFE_INTEGER,
  signal?: AbortSignal,
): Promise<AvifDimensionResult> {
  // Walk every top-level box header, instead of treating a prefix as a complete
  // file.  Metadata can legally be followed by a large mdat, but a truncated
  // or ambiguous tail must never be silently ignored before native decode.
  throwIfAborted(signal)
  const topLevelBoxes = await readTopLevelBoxes(file, signal)
  if (!topLevelBoxes) return { kind: 'invalid' }
  const meta = uniqueBox(topLevelBoxes, 'meta')
  const mdat = uniqueBox(topLevelBoxes, 'mdat')
  if (!meta || !mdat || meta.size > MAX_META_BOX_BYTES) return { kind: 'invalid' }
  const bytes = await readFileSlice(file, meta.dataOffset, meta.end, signal)
  if (bytes.length !== meta.size - meta.headerSize) return { kind: 'invalid' }

  // Dimensions must be associated with the primary item, not found by a
  // byte-pattern scan (thumbnail/auxiliary items can have their own `ispe`).
  const metaChildren = parseBoxes(bytes, 4, bytes.length)
  const itemInfo = readItemInfo(bytes, metaChildren.filter((box) => box.type === 'iinf'))
  if (!itemInfo) return { kind: 'invalid' }
  const decodeItems = new Set([...itemInfo].flatMap(([itemId, type]) => type === 'av01' ? [itemId] : []))
  if (decodeItems.size === 0 || decodeItems.size > MAX_DECODE_ITEMS) return { kind: 'invalid' }
  const primaryItem = readPrimaryItemId(bytes, uniqueBox(metaChildren, 'pitm'))
  if (primaryItem === null || !decodeItems.has(primaryItem)) return { kind: 'invalid' }
  const itemLocations = readItemLocations(
    bytes,
    uniqueBox(metaChildren, 'iloc'),
    itemInfo,
    decodeItems,
    file.size,
    mdat,
  )
  if (!itemLocations) return { kind: 'invalid' }
  const propertyBoxes = uniqueBox(metaChildren, 'iprp')
  const propertyChildren = propertyBoxes
    ? parseBoxes(bytes, propertyBoxes.dataOffset, propertyBoxes.end)
    : []
  const ipco = uniqueBox(propertyChildren, 'ipco')
  const ipcoProperties = ipco ? parseBoxes(bytes, ipco.dataOffset, ipco.end) : []
  const ispeByProperty = new Map<number, { width: number; height: number }>()
  for (const [index, box] of ipcoProperties.entries()) {
    if (box.type !== 'ispe') continue
    // ispe is a FullBox plus exactly two uint32 dimensions. Never turn a
    // malformed or zero extent into an implicit alpha extent.
    if (box.dataOffset + 12 !== box.end) return { kind: 'invalid' }
    const view = new DataView(bytes.buffer, bytes.byteOffset + box.dataOffset + 4, 8)
    const width = view.getUint32(0)
    const height = view.getUint32(4)
    if (width === 0 || height === 0) return { kind: 'invalid' }
    ispeByProperty.set(index + 1, { width, height })
  }
  const primaryProperties = readPrimaryPropertyIndexes(
    bytes,
    uniqueBox(propertyChildren, 'ipma'),
    primaryItem,
    ipcoProperties.length,
  )
  if (!primaryProperties) return { kind: 'invalid' }
  const primaryDimensions = primaryProperties
    .map((index) => ispeByProperty.get(index))
    .filter((value): value is { width: number; height: number } => value !== undefined)
  const primaryExtent = primaryDimensions[0]
  if (primaryDimensions.length !== 1 || !primaryExtent) return { kind: 'invalid' }
  const auxiliaryReferences = readAuxiliaryReferences(
    bytes,
    metaChildren.filter((box) => box.type === 'iref'),
  )
  const allProperties = readAllItemPropertyIndexes(
    bytes,
    uniqueBox(propertyChildren, 'ipma'),
    ipcoProperties.length,
  )
  if (!allProperties || allProperties.size > itemInfo.size) return { kind: 'invalid' }
  if ([...allProperties.keys()].some((itemId) => !itemInfo.has(itemId))) return { kind: 'invalid' }
  let cumulativePixels = 0
  for (const itemId of decodeItems) {
    throwIfAborted(signal)
    const indexes = allProperties.get(itemId)
    if (!indexes || indexes.length === 0) return { kind: 'invalid' }
    if (!itemLocations.has(itemId)) return { kind: 'invalid' }
    const alphaAuxiliary = hasKnownAlphaAuxiliary(
      bytes,
      indexes,
      ipcoProperties,
      itemId,
      primaryItem,
      auxiliaryReferences,
    )
    // Validate auxC/auxl before considering dimensions.
    if (alphaAuxiliary === null) return { kind: 'invalid' }
    let itemDimensions: { width: number; height: number } | undefined
    for (const index of indexes) {
      const dimensions = ispeByProperty.get(index)
      if (dimensions && !validateAvifDimensions(dimensions.width, dimensions.height, maxPixels)) {
        return { kind: 'too_large' }
      }
      if (dimensions) {
        if (itemDimensions) return { kind: 'invalid' }
        itemDimensions = dimensions
      }
    }
    // AVIF alpha auxiliary items use the primary item's extent by definition.
    // Keep that exceptional graph explicit; all unknown/derived items without
    // an `ispe` remain fail-closed before native decoding.
    if (!itemDimensions && alphaAuxiliary) {
      itemDimensions = primaryExtent
    }
    if (!itemDimensions) return { kind: 'invalid' }
    // AVIF alpha auxiliary items conventionally inherit the primary extent,
    // but some native-decodable encoders write a redundant ispe. Accept that
    // representation only when it exactly matches the primary item, so an
    // auxiliary plane cannot widen the allocation bound.
    if (
      alphaAuxiliary &&
      (itemDimensions.width !== primaryExtent.width || itemDimensions.height !== primaryExtent.height)
    ) return { kind: 'invalid' }
    const codedDimensions = await readAv1SequenceDimensions(file, itemLocations.get(itemId) ?? [], signal)
    // `ispe` is container metadata and is not an allocation bound for native
    // AV1 decoders. Require the coded sequence bounds to agree with it before
    // assigning the object URL to Image.src.
    if (!codedDimensions) return { kind: 'invalid' }
    if (
      codedDimensions.width > itemDimensions.width ||
      codedDimensions.height > itemDimensions.height ||
      !validateAvifDimensions(codedDimensions.width, codedDimensions.height, maxPixels)
    ) return { kind: 'too_large' }
    const pixels = itemDimensions.width * itemDimensions.height
    if (!Number.isSafeInteger(pixels) || cumulativePixels > maxPixels - pixels) {
      return { kind: 'too_large' }
    }
    cumulativePixels += pixels
  }
  return { kind: 'valid', ...primaryExtent }
}

export type AvifDimensionResult =
  | { kind: 'valid'; width: number; height: number }
  | { kind: 'invalid' }
  | { kind: 'too_large' }

function hasKnownAlphaAuxiliary(
  bytes: Uint8Array,
  propertyIndexes: number[],
  properties: ParsedBox[],
  itemId: number,
  primaryItem: number | null,
  auxiliaryReferences: Map<number, number[]> | null,
): boolean | null {
  let hasAlphaType = false
  for (const index of propertyIndexes) {
    const property = properties[index - 1]
    if (!property || property.type !== 'auxC') continue
    if (property.dataOffset + 4 >= property.end) return null
    // auxC is a FullBox followed by a null-terminated auxiliary type string.
    const typeBytes = bytes.subarray(property.dataOffset + 4, property.end)
    const nullOffset = typeBytes.indexOf(0)
    const version = bytes[property.dataOffset]
    const flags = ((bytes[property.dataOffset + 1] ?? 0) << 16) |
      ((bytes[property.dataOffset + 2] ?? 0) << 8) |
      (bytes[property.dataOffset + 3] ?? 0)
    // AVIF's auxiliary type property is auxC FullBox(0, 0). The auxiliary
    // type is a C string, so an unterminated alpha URN must not inherit the
    // primary extent before native decode.
    if (version !== 0 || flags !== 0 || nullOffset === -1) return null
    const auxiliaryType = new TextDecoder().decode(typeBytes.subarray(0, nullOffset))
    if (auxiliaryType === ALPHA_AUXILIARY_TYPE) hasAlphaType = true
  }
  if (!hasAlphaType) return false
  // An alpha auxiliary item must explicitly and uniquely point to the primary
  // item. Its optional ispe is checked against the primary extent by the
  // caller. Do not accept a standalone, duplicated or unrelated edge.
  const references = auxiliaryReferences?.get(itemId)
  if (
    primaryItem === null ||
    references?.length !== 1 ||
    references[0] !== primaryItem
  ) return null
  return true
}

function readAuxiliaryReferences(bytes: Uint8Array, boxes: ParsedBox[]): Map<number, number[]> | null {
  if (boxes.length !== 1) return null
  const box = boxes[0]
  if (!box || box.dataOffset + 4 > box.end) return null
  const version = bytes[box.dataOffset] ?? 0
  if (version !== 0 && version !== 1) return null
  const idBytes = version === 0 ? 2 : 4
  const references = new Map<number, number[]>()
  for (const entry of parseBoxes(bytes, box.dataOffset + 4, box.end)) {
    if (entry.type !== 'auxl' || entry.dataOffset + idBytes + 2 > entry.end) continue
    const view = new DataView(bytes.buffer, bytes.byteOffset + entry.dataOffset, entry.end - entry.dataOffset)
    const fromItemId = idBytes === 2 ? view.getUint16(0) : view.getUint32(0)
    const count = view.getUint16(idBytes)
    if (fromItemId === 0 || count !== 1 || entry.dataOffset + idBytes + 2 + count * idBytes !== entry.end) return null
    const toItemId = idBytes === 2 ? view.getUint16(idBytes + 2) : view.getUint32(idBytes + 2)
    if (toItemId === 0 || references.has(fromItemId)) return null
    references.set(fromItemId, [toItemId])
  }
  return references
}

function readItemInfo(bytes: Uint8Array, boxes: ParsedBox[]): Map<number, string> | null {
  // `iinf` is the authoritative item graph. Missing or duplicate tables used
  // to bypass derived-item checks, so do not let either reach native decode.
  if (boxes.length !== 1) return null
  const box = boxes[0]
  if (!box || box.dataOffset + 8 > box.end) return null
  const version = bytes[box.dataOffset] ?? 0
  if (version !== 0 && version !== 1) return null
  const entryCountOffset = box.dataOffset + 4
  // ISO-BMFF iinf stores entry_count as uint16 only in version 0; version 1
  // and later use uint32. Reading v1 as uint16 can desynchronize nested infe
  // parsing and let a derived item reach native decode.
  const entryCountBytes = version >= 1 ? 4 : 2
  if (entryCountOffset + entryCountBytes > box.end) return null
  const view = new DataView(bytes.buffer, bytes.byteOffset + entryCountOffset, entryCountBytes)
  const entryCount = version >= 1 ? view.getUint32(0) : view.getUint16(0)
  const entries = parseBoxes(bytes, entryCountOffset + entryCountBytes, box.end)
  if (entryCount === 0 || entryCount > MAX_ITEM_INFO_ENTRIES || entries.length !== entryCount) return null
  const items = new Map<number, string>()
  for (const entry of entries) {
    if (entry.type !== 'infe' || entry.dataOffset + 8 > entry.end) return null
    const infeVersion = bytes[entry.dataOffset] ?? 0
    // AVIF requires the explicit item type available in infe v2/v3. Older
    // versions cannot prove that an item is not a grid/overlay derivative.
    if (infeVersion !== 2 && infeVersion !== 3) return null
    const itemIdOffset = entry.dataOffset + 4
    const typeOffset = infeVersion === 2 ? entry.dataOffset + 8 : entry.dataOffset + 10
    // `item_name` is a required null-terminated string after item_type.
    // Accepting a truncated infe here would let an incomplete item graph
    // reach the native decoder.
    if (typeOffset + 5 > entry.end) return null
    const itemId = infeVersion === 2
      ? new DataView(bytes.buffer, bytes.byteOffset + itemIdOffset, 2).getUint16(0)
      : new DataView(bytes.buffer, bytes.byteOffset + itemIdOffset, 4).getUint32(0)
    const type = readFourCc(bytes, typeOffset)
    const itemNameOffset = typeOffset + 4
    const itemNameEnd = bytes.subarray(itemNameOffset, entry.end).indexOf(0)
    if (itemNameEnd === -1) return null
    // The full inventory may contain non-decode-bearing metadata items. Keep
    // their syntax bounded, but only av01 entries join the graph that is
    // dimension-checked and passed to native decode.
    if (type === 'mime') {
      const contentTypeOffset = itemNameOffset + itemNameEnd + 1
      const contentTypeEnd = bytes.subarray(contentTypeOffset, entry.end).indexOf(0)
      if (contentTypeEnd <= 0) return null
    } else if (type !== 'av01' && type !== 'Exif') {
      return null
    }
    if (itemId === 0 || items.has(itemId)) return null
    items.set(itemId, type)
  }
  return items
}

function uniqueBox(boxes: ParsedBox[], type: string): ParsedBox | undefined {
  const matches = boxes.filter((box) => box.type === type)
  return matches.length === 1 ? matches[0] : undefined
}

function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) throw new DOMException('AVIF preflight was aborted', 'AbortError')
}

async function readFileSlice(
  file: File,
  start: number,
  end: number,
  signal?: AbortSignal,
): Promise<Uint8Array> {
  throwIfAborted(signal)
  const bytes = new Uint8Array(await file.slice(start, end).arrayBuffer())
  throwIfAborted(signal)
  return bytes
}

async function readTopLevelBoxes(file: File, signal?: AbortSignal): Promise<ParsedBox[] | null> {
  if (file.size < 16 || file.size > MAX_AVIF_FILE_BYTES) return null
  const boxes: ParsedBox[] = []
  let offset = 0
  while (offset < file.size) {
    throwIfAborted(signal)
    if (boxes.length >= MAX_TOP_LEVEL_BOXES || file.size - offset < 8) return null
    const header = await readFileSlice(file, offset, Math.min(file.size, offset + 16), signal)
    if (header.length < 8) return null
    const shortSize = new DataView(header.buffer, header.byteOffset, header.byteLength).getUint32(0)
    const headerSize = shortSize === 1 ? 16 : 8
    if (header.length < headerSize) return null
    const size = shortSize === 0
      ? file.size - offset
      : shortSize === 1
        ? Number(new DataView(header.buffer, header.byteOffset + 8, 8).getBigUint64(0))
        : shortSize
    if (!Number.isSafeInteger(size) || size < headerSize || size > file.size - offset) return null
    boxes.push({
      type: readFourCc(header, 4),
      size,
      headerSize,
      dataOffset: offset + headerSize,
      end: offset + size,
    })
    offset += size
  }
  return offset === file.size ? boxes : null
}

function readUnsigned(view: DataView, offset: number, bytes: number): number | null {
  if (bytes < 0 || bytes > 8 || offset + bytes > view.byteLength) return null
  if (bytes === 0) return 0
  let value = 0n
  for (let index = 0; index < bytes; index += 1) value = (value << 8n) | BigInt(view.getUint8(offset + index))
  const number = Number(value)
  return Number.isSafeInteger(number) ? number : null
}

function readItemLocations(
  bytes: Uint8Array,
  box: ParsedBox | undefined,
  items: Map<number, string>,
  decodeItems: Set<number>,
  fileSize: number,
  mdat: ParsedBox,
): Map<number, Av1Extent[]> | null {
  if (!box || box.dataOffset + 8 > box.end) return null
  const version = bytes[box.dataOffset]
  if (version !== 0 && version !== 1 && version !== 2) return null
  const view = new DataView(bytes.buffer, bytes.byteOffset + box.dataOffset + 4, box.end - box.dataOffset - 4)
  if (view.byteLength < 4) return null
  const sizes = view.getUint16(0)
  const offsetSize = sizes >> 12
  const lengthSize = (sizes >> 8) & 0x0f
  const baseOffsetSize = (sizes >> 4) & 0x0f
  const indexSize = version === 0 ? 0 : sizes & 0x0f
  if (offsetSize > 8 || lengthSize === 0 || lengthSize > 8 || baseOffsetSize > 8 || indexSize > 8) return null
  let offset = 2
  const itemCountBytes = version < 2 ? 2 : 4
  const itemCount = readUnsigned(view, offset, itemCountBytes)
  if (itemCount === null || itemCount > items.size) return null
  offset += itemCountBytes
  const locations = new Map<number, Av1Extent[]>()
  const occupied: Array<{ start: number; end: number }> = []
  let totalPayload = 0
  for (let item = 0; item < itemCount; item += 1) {
    const itemId = readUnsigned(view, offset, version < 2 ? 2 : 4)
    if (itemId === null || !items.has(itemId) || locations.has(itemId)) return null
    offset += version < 2 ? 2 : 4
    if (version > 0) {
      const construction = readUnsigned(view, offset, 2)
      if (construction === null || (construction & 0x000f) !== 0) return null
      offset += 2
    }
    const dataReference = readUnsigned(view, offset, 2)
    if (dataReference !== 0) return null
    offset += 2
    const baseOffset = readUnsigned(view, offset, baseOffsetSize)
    if (baseOffset === null) return null
    offset += baseOffsetSize
    const extentCount = readUnsigned(view, offset, 2)
    if (extentCount === null || extentCount === 0 || extentCount > MAX_EXTENTS_PER_ITEM) return null
    offset += 2
    const extents: Av1Extent[] = []
    for (let extent = 0; extent < extentCount; extent += 1) {
      if (version > 0) {
        if (readUnsigned(view, offset, indexSize) === null) return null
        offset += indexSize
      }
      const relativeOffset = readUnsigned(view, offset, offsetSize)
      if (relativeOffset === null) return null
      offset += offsetSize
      const length = readUnsigned(view, offset, lengthSize)
      if (length === null || length === 0 || baseOffset > Number.MAX_SAFE_INTEGER - relativeOffset) return null
      offset += lengthSize
      const start = baseOffset + relativeOffset
      if (start > Number.MAX_SAFE_INTEGER - length) return null
      const end = start + length
      if (start < mdat.dataOffset || end > mdat.end || end > fileSize || totalPayload > MAX_AVIF_FILE_BYTES - length) return null
      for (const occupiedExtent of occupied) {
        if (start < occupiedExtent.end && end > occupiedExtent.start) return null
      }
      occupied.push({ start, end })
      extents.push({ start, end })
      totalPayload += length
    }
    if (decodeItems.has(itemId)) locations.set(itemId, extents)
  }
  return offset === view.byteLength && totalPayload > 0 && locations.size === decodeItems.size ? locations : null
}

interface Av1Extent {
  start: number
  end: number
}

async function readAv1SequenceDimensions(
  file: File,
  extents: Av1Extent[],
  signal?: AbortSignal,
): Promise<{ width: number; height: number } | null> {
  let remaining = MAX_SEQUENCE_HEADER_SCAN_BYTES
  const chunks: Uint8Array[] = []
  for (const extent of extents) {
    throwIfAborted(signal)
    if (remaining === 0) break
    const length = Math.min(extent.end - extent.start, remaining)
    const chunk = await readFileSlice(file, extent.start, extent.start + length, signal)
    if (chunk.length !== length) return null
    chunks.push(chunk)
    remaining -= length
  }
  const bytes = new Uint8Array(chunks.reduce((size, chunk) => size + chunk.length, 0))
  let offset = 0
  for (const chunk of chunks) {
    bytes.set(chunk, offset)
    offset += chunk.length
  }
  return parseAv1SequenceHeader(bytes)
}

function parseAv1SequenceHeader(bytes: Uint8Array): { width: number; height: number } | null {
  let offset = 0
  while (offset < bytes.length) {
    const header = bytes[offset++]
    if (header === undefined || (header & 0x80) !== 0 || (header & 0x01) !== 0) return null
    const type = (header >> 3) & 0x0f
    const hasExtension = (header & 0x04) !== 0
    const hasSize = (header & 0x02) !== 0
    if (hasExtension) {
      if (offset >= bytes.length) return null
      offset += 1
    }
    let payloadStart = offset
    let payloadEnd = bytes.length
    if (hasSize) {
      const payloadLength = readLeb128(bytes, offset)
      if (!payloadLength || payloadLength.value > bytes.length - payloadLength.next) return null
      payloadStart = payloadLength.next
      payloadEnd = payloadStart + payloadLength.value
    }
    if (type === 1) return parseSequenceHeaderBits(bytes.subarray(payloadStart, payloadEnd))
    if (!hasSize) return null
    offset = payloadEnd
  }
  return null
}

function readLeb128(bytes: Uint8Array, offset: number): { value: number; next: number } | null {
  let value = 0
  for (let index = 0; index < 8; index += 1) {
    const byte = bytes[offset + index]
    if (byte === undefined || value > Number.MAX_SAFE_INTEGER / 128) return null
    value += (byte & 0x7f) * 2 ** (index * 7)
    if ((byte & 0x80) === 0) return Number.isSafeInteger(value) ? { value, next: offset + index + 1 } : null
  }
  return null
}

function parseSequenceHeaderBits(bytes: Uint8Array): { width: number; height: number } | null {
  const bits = new BitReader(bytes)
  if (bits.read(3) === null || bits.read(1) === null) return null
  const reducedStillPictureHeader = bits.read(1)
  if (reducedStillPictureHeader === null) return null
  if (reducedStillPictureHeader === 1) {
    if (bits.read(5) === null) return null
  } else if (!skipNonReducedSequenceHeader(bits)) {
    return null
  }
  const widthBits = bits.read(4)
  const heightBits = bits.read(4)
  if (widthBits === null || heightBits === null) return null
  const width = bits.read(widthBits + 1)
  const height = bits.read(heightBits + 1)
  return width === null || height === null ? null : { width: width + 1, height: height + 1 }
}

function skipNonReducedSequenceHeader(bits: BitReader): boolean {
  const timingInfoPresent = bits.read(1)
  if (timingInfoPresent === null) return false
  if (timingInfoPresent === 1) {
    if (bits.read(32) === null || bits.read(32) === null) return false
    const equalPictureInterval = bits.read(1)
    if (equalPictureInterval === null || (equalPictureInterval === 1 && !bits.skipUvlc())) return false
    const decoderModelInfoPresent = bits.read(1)
    if (decoderModelInfoPresent === null) return false
    let bufferDelayLength: number | null = null
    if (decoderModelInfoPresent === 1) {
      const bufferDelayLengthMinusOne = bits.read(5)
      if (
        bufferDelayLengthMinusOne === null ||
        bits.read(32) === null ||
        bits.read(5) === null ||
        bits.read(5) === null
      ) return false
      bufferDelayLength = bufferDelayLengthMinusOne + 1
    }
    const initialDisplayDelayPresent = bits.read(1)
    const operatingPointsMinusOne = bits.read(5)
    if (initialDisplayDelayPresent === null || operatingPointsMinusOne === null) return false
    for (let index = 0; index <= operatingPointsMinusOne; index += 1) {
      if (bits.read(12) === null) return false
      const level = bits.read(5)
      if (level === null || (level > 7 && bits.read(1) === null)) return false
      if (decoderModelInfoPresent === 1) {
        const present = bits.read(1)
        if (
          present === null ||
          (present === 1 && (
            bufferDelayLength === null ||
            bits.read(bufferDelayLength) === null ||
            bits.read(bufferDelayLength) === null ||
            bits.read(1) === null
          ))
        ) return false
      }
      if (initialDisplayDelayPresent === 1) {
        const present = bits.read(1)
        if (present === null || (present === 1 && bits.read(4) === null)) return false
      }
    }
  } else {
    // decoder_model_info_present_flag and all decoder-model fields are present
    // only when timing_info_present_flag is set in the AV1 grammar.
    const initialDisplayDelayPresent = bits.read(1)
    const operatingPointsMinusOne = bits.read(5)
    if (initialDisplayDelayPresent === null || operatingPointsMinusOne === null) return false
    for (let index = 0; index <= operatingPointsMinusOne; index += 1) {
      if (bits.read(12) === null) return false
      const level = bits.read(5)
      if (level === null || (level > 7 && bits.read(1) === null)) return false
      if (initialDisplayDelayPresent === 1) {
        const present = bits.read(1)
        if (present === null || (present === 1 && bits.read(4) === null)) return false
      }
    }
  }
  return true
}

class BitReader {
  private offset = 0

  constructor(private readonly bytes: Uint8Array) {}

  read(length: number): number | null {
    if (length < 0 || length > 32 || this.offset + length > this.bytes.length * 8) return null
    let value = 0
    for (let index = 0; index < length; index += 1) {
      value = (value << 1) | ((this.bytes[(this.offset + index) >> 3] ?? 0) >> (7 - ((this.offset + index) & 7)) & 1)
    }
    this.offset += length
    return value
  }

  skipUvlc(): boolean {
    let leadingZeros = 0
    while (this.read(1) === 0) {
      leadingZeros += 1
      if (leadingZeros > 31) return false
    }
    return this.read(leadingZeros) !== null
  }
}

interface ParsedBox {
  type: string
  size: number
  headerSize: number
  dataOffset: number
  end: number
}

function parseBoxes(bytes: Uint8Array, start: number, end: number): ParsedBox[] {
  const result: ParsedBox[] = []
  let offset = start
  while (offset + 8 <= end) {
    const view = new DataView(bytes.buffer, bytes.byteOffset + offset, 8)
    const shortSize = view.getUint32(0)
    const type = readFourCc(bytes, offset + 4)
    const headerSize = shortSize === 1 ? 16 : 8
    // An extended-size box must include its complete 16-byte header. Returning
    // the parsed prefix here would let an incomplete child be treated as an
    // otherwise valid parent container.
    if (offset + headerSize > end) return []
    const size = shortSize === 0
      ? end - offset
      : shortSize === 1
        ? Number(new DataView(bytes.buffer, bytes.byteOffset + offset + 8, 8).getBigUint64(0))
        : shortSize
    if (!Number.isSafeInteger(size) || size < headerSize) return []
    // This parser is only used for a declared parent range. A child extending
    // beyond that range makes the entire container malformed; returning a
    // parsed prefix would allow callers to accept incomplete metadata.
    if (offset + size > end) return []
    result.push({ type, size, headerSize, dataOffset: offset + headerSize, end: offset + size })
    offset += size
  }
  return offset === end ? result : []
}

function readPrimaryItemId(bytes: Uint8Array, box: ParsedBox | undefined): number | null {
  if (!box || box.dataOffset + 6 > box.end) return null
  try {
    const version = bytes[box.dataOffset] ?? 0
    const view = new DataView(bytes.buffer, bytes.byteOffset + box.dataOffset + 4, box.end - box.dataOffset - 4)
    return version === 0 ? view.getUint16(0) : view.getUint32(0)
  } catch {
    return null
  }
}

function readPrimaryPropertyIndexes(
  bytes: Uint8Array,
  box: ParsedBox | undefined,
  primaryItem: number | null,
  propertyCount: number,
): number[] | null {
  if (!box || primaryItem === null || box.dataOffset + 8 > box.end) return null
  const version = bytes[box.dataOffset] ?? 0
  const view = new DataView(bytes.buffer, bytes.byteOffset + box.dataOffset + 4, box.end - box.dataOffset - 4)
  const flags = ((bytes[box.dataOffset + 1] ?? 0) << 16) |
    ((bytes[box.dataOffset + 2] ?? 0) << 8) |
    (bytes[box.dataOffset + 3] ?? 0)
  let offset = 0
  const entryCount = view.getUint32(0)
  offset += 4
  let primaryIndexes: number[] | null = null
  for (let entry = 0; entry < entryCount; entry += 1) {
    if (offset + (version < 1 ? 2 : 4) + 1 > view.byteLength) return null
    const itemId = version < 1 ? view.getUint16(offset) : view.getUint32(offset)
    offset += version < 1 ? 2 : 4
    const associationCount = view.getUint8(offset)
    offset += 1
    const associationSize = (flags & 1) === 0 ? 1 : 2
    const associationBytes = associationCount * associationSize
    if (offset + associationBytes > view.byteLength) return null
    if (itemId !== primaryItem) {
      offset += associationBytes
      continue
    }
    if (primaryIndexes !== null) return null
    const indexes: number[] = []
    for (let association = 0; association < associationCount; association += 1) {
      const value = associationSize === 1 ? view.getUint8(offset) : view.getUint16(offset)
      const propertyIndex = value & (associationSize === 1 ? 0x7f : 0x7fff)
      // An association index of zero means "no property" in ISO-BMFF, but
      // AVIF dimensions must be fully proven before native decode. Reject it
      // (and indexes beyond ipco) instead of silently ignoring the entry.
      if (propertyIndex === 0 || propertyIndex > propertyCount) return null
      indexes.push(propertyIndex)
      offset += associationSize
    }
    primaryIndexes = indexes
  }
  return offset === view.byteLength ? primaryIndexes : null
}

function readAllItemPropertyIndexes(
  bytes: Uint8Array,
  box: ParsedBox | undefined,
  propertyCount: number,
): Map<number, number[]> | null {
  if (!box || box.dataOffset + 8 > box.end) return null
  const version = bytes[box.dataOffset] ?? 0
  const flags = ((bytes[box.dataOffset + 1] ?? 0) << 16) |
    ((bytes[box.dataOffset + 2] ?? 0) << 8) |
    (bytes[box.dataOffset + 3] ?? 0)
  const view = new DataView(bytes.buffer, bytes.byteOffset + box.dataOffset + 4, box.end - box.dataOffset - 4)
  if (view.byteLength < 4) return null
  const entryCount = view.getUint32(0)
  let offset = 4
  const result = new Map<number, number[]>()
  const idBytes = version < 1 ? 2 : 4
  const associationSize = (flags & 1) === 0 ? 1 : 2
  for (let entry = 0; entry < entryCount; entry += 1) {
    if (offset + idBytes + 1 > view.byteLength) return null
    const itemId = idBytes === 2 ? view.getUint16(offset) : view.getUint32(offset)
    offset += idBytes
    const associationCount = view.getUint8(offset)
    offset += 1
    const associationBytes = associationCount * associationSize
    if (offset + associationBytes > view.byteLength || result.has(itemId)) return null
    const indexes: number[] = []
    for (let association = 0; association < associationCount; association += 1) {
      const value = associationSize === 1 ? view.getUint8(offset) : view.getUint16(offset)
      const propertyIndex = value & (associationSize === 1 ? 0x7f : 0x7fff)
      if (propertyIndex === 0 || propertyIndex > propertyCount) return null
      indexes.push(propertyIndex)
      offset += associationSize
    }
    result.set(itemId, indexes)
  }
  return offset === view.byteLength ? result : null
}
